/**
 * Эргэлдэх мэндчилгээ — дашбоардын дээд тууз.
 *
 * ⚠️ ЦАГИЙН БҮС: бүлгийг `Asia/Ulaanbaatar`-аар сонгоно, хөтчийн
 *    орон нутгийн цагаар БИШ. Цуглуулагч Оросод (UTC+3…+8) ажилладаг
 *    тул түүний утасны цагаар бол «оройн мэнд» гэж УБ-д өглөө болж
 *    байхад гарна. CLAUDE.md: «Клиент Asia/Ulaanbaatar руу хөрвүүлнэ».
 *
 * ⚠️ ХӨДӨЛГӨӨН БАГАСГАХ: `prefers-reduced-motion: reduce` үед эргэлт
 *    ОГТ эхлэхгүй — эхний мэндчилгээ статик үлдэнэ. Зөвхөн CSS-ийн
 *    transition-ийг унтраагаад орхивол текст огцом солигдож, вестибуляр
 *    мэдрэмжтэй хэрэглэгчид улам муу болно.
 */

import { useEffect, useMemo, useState } from 'react';

/** Мэндчилгээ — нэрийг ард нь залгана. Бүлэг тус бүр 2, нийт 6. */
const GREETINGS = {
  morning: ['Өглөөний мэнд', 'Сайхан өглөө'],
  day: ['Өдрийн мэнд', 'Сайн байна уу'],
  evening: ['Оройн мэнд', 'Ажил амжуулж байна уу'],
} as const;

type Slot = keyof typeof GREETINGS;

/** Дараагийн мэндчилгээ рүү шилжих хугацаа. 400ms анимацитай зохицсон. */
const ROTATE_MS = 5000;

const REDUCED_MOTION = '(prefers-reduced-motion: reduce)';

/** Улаанбаатарын цагаар одоо хэдэн цаг болж байгаа вэ (0–23). */
function hourInUlaanbaatar(now: Date): number {
  const h = new Intl.DateTimeFormat('en-GB', {
    timeZone: 'Asia/Ulaanbaatar',
    hour: '2-digit',
    hour12: false,
  }).format(now);
  // Зарим хөтөч шөнө дундыг "24" гэж буцаадаг — 0 болгож нормчилно
  return Number(h) % 24;
}

function slotFor(hour: number): Slot {
  if (hour >= 5 && hour < 12) return 'morning';
  if (hour >= 12 && hour < 18) return 'day';
  return 'evening';
}

export interface RotatingGreeting {
  /** Тухайн цагийн бүлгийн БҮХ мэндчилгээ — grid stack-д бүгд рендерлэгдэнэ */
  greetings: string[];
  /** Одоо харагдаж буйн индекс */
  index: number;
}

/**
 * @param name Хэрэглэгчийн бүтэн нэр. Байхгүй бол нэргүй мэндчилнэ.
 */
export function useRotatingGreeting(name: string | undefined): RotatingGreeting {
  const [slot, setSlot] = useState<Slot>(() => slotFor(hourInUlaanbaatar(new Date())));
  const [index, setIndex] = useState(0);

  const greetings = useMemo(
    () => GREETINGS[slot].map((g) => (name ? `${g}, ${name}` : g)),
    [slot, name],
  );

  useEffect(() => {
    const mq = window.matchMedia(REDUCED_MOTION);
    let timer: ReturnType<typeof setInterval> | undefined;

    const tick = () => {
      // Хуудас удаан нээлттэй байвал 11:59 → 12:00 дээр бүлэг солигдоно.
      // Индексийг тэглэх шаардлагагүй — буцаахдаа уртаар нь модул авна.
      const next = slotFor(hourInUlaanbaatar(new Date()));
      setSlot((prev) => (prev === next ? prev : next));
      setIndex((i) => i + 1);
    };

    const sync = () => {
      clearInterval(timer);
      timer = undefined;
      if (mq.matches) {
        setIndex(0); // статик руу буцаана
        return;
      }
      timer = setInterval(tick, ROTATE_MS);
    };

    sync();
    mq.addEventListener('change', sync);
    return () => {
      clearInterval(timer);
      mq.removeEventListener('change', sync);
    };
  }, []);

  // Бүлэг солигдох агшинд индекс хязгаараас гарахаас сэргийлнэ
  return { greetings, index: greetings.length ? index % greetings.length : 0 };
}
